import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  AlertOctagon, AlertTriangle, Truck as TruckIcon, DollarSign, BarChart3, TrendingUp, ShieldAlert, Compass, Fuel,
  ArrowRight, ArrowUp, ArrowDown, ChevronRight,
} from "lucide-react";
import { apiBaseUrl, authHeaders, isAdmin } from "./shared";
import { useAlerts } from "../../hooks/useAlerts";
import LoadingState from "./LoadingState";
import Pagination from "./Pagination";

// Small local copy, same convention DispatcherHome.jsx already uses --
// this page only needs the tab a given alert type should deep-link into,
// not Alerts.jsx's full icon/stat formatter per type.
const ALERT_TAB_BY_TYPE = {
  eta_drift: "route", pto_conflict: "overview", returning_driver_available: "overview",
  off_route: "route", toll_overage: "route", hos_violation: "route", fuel_deviation: "route",
  pickup_overdue: "overview", pickup_risk: "route", delivery_risk: "route",
  possible_missed_pickup: "overview", possible_missed_delivery: "overview",
};

// Stop-detection completion CANDIDATES -- a departure-after-dwell with no
// confirmed pickup/delivery. Distinct from "currently at facility"
// (ARRIVED/DWELLING, surfaced live in the DispatcherBoard reminder panel):
// these trucks have ALREADY LEFT, so they are a possible MISSED lifecycle
// event, not a truck still sitting at the dock.
const MISSED_STOP_TYPES = new Set(["possible_missed_pickup", "possible_missed_delivery"]);

const fmtMoney = (n) => {
  if (n == null) return "—";
  const sign = n < 0 ? "-" : "";
  return `${sign}$${Math.abs(n).toLocaleString("en-US", { maximumFractionDigits: 0 })}`;
};

// Purely internal -- decides sort order for Needs Attention only, never
// rendered as a number anywhere. severity dominates, cost/urgency are
// tie-breakers within a severity band. Taking abs() of two already-honest
// numbers purely as a ranking heuristic doesn't violate the "never sum
// estimated + verified for a displayed figure" rule -- nothing here is
// ever shown to the user.
const SEVERITY_SCORE = { critical: 100, warning: 50, info: 10 };
function impactScore(a) {
  const costMagnitude = Math.abs(a.estimated_cost_impact || 0) + Math.abs(a.verified_cost_impact || 0);
  let urgency = 0;
  if (a.type === "eta_drift") urgency = Math.abs(a.drift_hours || 0) * 4;
  else if (a.type === "hos_violation") urgency = (a.hours_over || 0) * 6 + 15;
  else if (a.type === "off_route") urgency = (a.distance_from_route_miles || 0) * 0.3;
  else if (a.type === "pto_conflict") urgency = 20;
  return (SEVERITY_SCORE[a.severity] || 0) + Math.min(40, costMagnitude / 25) + Math.min(30, urgency);
}

function fmtDuration(ms) {
  if (ms < 0) ms = 0;
  const totalMinutes = Math.floor(ms / 60000);
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  if (h === 0) return `${m}m`;
  return `${h}h ${m}m`;
}

const ATTENTION_PAGE_SIZE = 10;
const CAPACITY_PAGE_SIZE = 25;

// Shared by both roles: what's wrong right now, what it's costing, where
// to look first -- almost entirely a recomposition of existing signals
// (fleet_alert_events via /fleet/alerts + its impact rollup,
// fuel_compliance_by_truck, dispatcher_scorecard, available_capacity_by_
// dispatcher), all of which already auto-scope server-side by caller role.
// Only two sections differ by role (Dispatcher Overview -> "My Performance",
// Fleet Economics -> "My Economics") -- everything else renders identically
// for admin and dispatcher, just against a smaller, pre-scoped dataset for
// a dispatcher. Replaced both AdminFleetOverview and DispatcherHome as the
// role-branched home page; exceptions should scream, KPIs should inform.
export default function ControlTower() {
  const admin = isAdmin();
  const myId = localStorage.getItem("id");
  const { alerts: rawAlerts } = useAlerts(apiBaseUrl, authHeaders);
  // eta_drift demoted out of the operator-facing Control Tower (audited this
  // session -- of 41 open eta_drift rows on prod, 7 duplicated a harder
  // Action Required exception on the same load and the other 34 were stale/
  // placeholder Check Load records still comfortably within their real
  // delivery slack per the trustworthy pickup_risk/delivery_risk engine.
  // Zero added unique, actionable information as a dispatcher-facing alert
  // -- it measures drift from the originally quoted plan, not threat to the
  // real commitment. The detector and its historical rows are untouched;
  // this just stops it from feeding Needs Attention or the health tiles
  // here. Not touching useAlerts' own counts, since those are shared with
  // the sidebar bell/Alerts.jsx, which may still want eta_drift for
  // per-truck diagnostic context -- recomputed locally instead.
  const alerts = useMemo(() => rawAlerts.filter((a) => a.type !== "eta_drift"), [rawAlerts]);
  const alertCounts = useMemo(() => {
    const c = { critical: 0, warning: 0, info: 0 };
    for (const a of alerts) {
      if (c[a.severity] != null) c[a.severity] += 1;
    }
    return c;
  }, [alerts]);
  const [scorecard, setScorecard] = useState(null);
  const [capacity, setCapacity] = useState(null);
  const [fuelCompliance, setFuelCompliance] = useState(null);
  const [impact24h, setImpact24h] = useState(null);
  const [impact7d, setImpact7d] = useState(null);
  const [impact30d, setImpact30d] = useState(null);
  const [loading, setLoading] = useState(true);
  const [attentionPage, setAttentionPage] = useState(1);
  const [capacityPage, setCapacityPage] = useState(1);
  const navigate = useNavigate();

  useEffect(() => {
    setLoading(true);
    Promise.all([
      axios.get(`${apiBaseUrl}/scripted/fleet/dispatcher-scorecard`, authHeaders()),
      axios.get(`${apiBaseUrl}/scripted/fleet/fuel-compliance`, { ...authHeaders(), params: { page_size: 200 } }),
      axios.get(`${apiBaseUrl}/scripted/fleet/alert-impact-summary`, { ...authHeaders(), params: { days: 1 } }),
      axios.get(`${apiBaseUrl}/scripted/fleet/alert-impact-summary`, { ...authHeaders(), params: { days: 7 } }),
      axios.get(`${apiBaseUrl}/scripted/fleet/alert-impact-summary`, { ...authHeaders(), params: { days: 30 } }),
    ])
      .then(([sc, fuel, i24, i7, i30]) => {
        setScorecard(sc.data);
        setFuelCompliance(fuel.data);
        setImpact24h(i24.data);
        setImpact7d(i7.data);
        setImpact30d(i30.data);
      })
      .finally(() => setLoading(false));
  }, []);

  // Available Capacity is paginated server-side, so it gets its own effect
  // keyed on capacityPage -- refetching the other four endpoints every time
  // an admin pages through trucks would be wasteful, same "live/paginated
  // data in its own effect" split DispatcherHome.jsx already uses.
  useEffect(() => {
    axios
      .get(`${apiBaseUrl}/scripted/fleet/available-capacity`, { ...authHeaders(), params: { page: capacityPage, page_size: CAPACITY_PAGE_SIZE } })
      .then((res) => setCapacity(res.data))
      .catch(() => {});
  }, [capacityPage]);

  // Operational exception beats projected risk: a load with an open
  // pickup_overdue (category "operational_exception") has already missed
  // its appointment -- that's a realized fact, not a projection anymore.
  // Both _check_pickup_overdue_events and _check_delivery_commitment_risk
  // can legitimately stay open on the SAME load_id at once (by design, see
  // their own docstrings), so without this de-dup a load would double-count:
  // shown under Action Required AND still inflating Loads At Risk AND still
  // reappearing in Needs Attention as a pickup_risk row. exceptionLoadIds is
  // built first so every downstream list/count can be filtered against it.
  const operationalExceptions = useMemo(
    () => alerts.filter((a) => a.category === "operational_exception"),
    [alerts]
  );
  const exceptionLoadIds = useMemo(
    () => new Set(operationalExceptions.map((a) => a.load_id).filter(Boolean)),
    [operationalExceptions]
  );
  const needsAttentionAlerts = useMemo(
    () =>
      alerts.filter((a) => {
        if (a.category === "operational_exception") return false; // shown under Action Required instead
        if (["pickup_risk", "delivery_risk"].includes(a.type) && exceptionLoadIds.has(a.load_id)) return false; // superseded mirror
        return true;
      }),
    [alerts, exceptionLoadIds]
  );
  // Split the two operational-exception concepts so each renders with its own
  // (correct) headline: realized pickup-overdue vs possible missed lifecycle.
  const sortedActionRequired = useMemo(
    () => operationalExceptions
      .filter((a) => !MISSED_STOP_TYPES.has(a.type))
      .sort((a, b) => (b.overdue_minutes || 0) - (a.overdue_minutes || 0)),
    [operationalExceptions]
  );
  const CONF_RANK = { VERY_HIGH: 0, HIGH: 1, MEDIUM: 2, LOW: 3 };
  const missedStopCandidates = useMemo(
    () => operationalExceptions
      .filter((a) => MISSED_STOP_TYPES.has(a.type))
      .sort((a, b) => (CONF_RANK[a.confidence] ?? 9) - (CONF_RANK[b.confidence] ?? 9)),
    [operationalExceptions]
  );
  const sortedAlerts = useMemo(
    () => [...needsAttentionAlerts].sort((a, b) => impactScore(b) - impactScore(a)),
    [needsAttentionAlerts]
  );
  const attentionTotalPages = Math.max(1, Math.ceil(sortedAlerts.length / ATTENTION_PAGE_SIZE));
  const pagedAlerts = useMemo(
    () => sortedAlerts.slice((attentionPage - 1) * ATTENTION_PAGE_SIZE, attentionPage * ATTENTION_PAGE_SIZE),
    [sortedAlerts, attentionPage]
  );
  // useAlerts polls every 60s -- the list can shrink between polls, which
  // would otherwise strand an admin on a now-out-of-range page showing
  // nothing with live-looking Prev/Next buttons.
  useEffect(() => {
    if (attentionPage > attentionTotalPages) setAttentionPage(1);
  }, [attentionTotalPages, attentionPage]);

  // Distinct loads with an open pickup_risk/delivery_risk buffer-based
  // projection, EXCLUDING any load that already has a harder operational
  // exception open (see exceptionLoadIds above) -- those loads have already
  // missed their appointment, so counting them here too would inflate this
  // tile with loads that are no longer merely "at risk."
  const loadsAtRiskCount = useMemo(() => {
    const ids = new Set();
    for (const a of alerts) {
      if (["pickup_risk", "delivery_risk"].includes(a.type) && a.load_id && !exceptionLoadIds.has(a.load_id)) {
        ids.add(a.load_id);
      }
    }
    return ids.size;
  }, [alerts, exceptionLoadIds]);

  const capacityByDispatcher = useMemo(() => {
    const trucks = capacity?.trucks || [];
    const byDispatcher = new Map();
    for (const t of trucks) {
      const key = t.dispatcher_name || "Unassigned";
      if (!byDispatcher.has(key)) byDispatcher.set(key, []);
      byDispatcher.get(key).push(t);
    }
    return [...byDispatcher.entries()].sort(([a], [b]) => {
      if (a === "Unassigned") return 1;
      if (b === "Unassigned") return -1;
      return a.localeCompare(b);
    });
  }, [capacity]);

  const dispatchers = useMemo(() => (scorecard?.dispatchers || []).filter((d) => d.dispatcher_id), [scorecard]);
  const fleet = scorecard?.fleet;
  const me = useMemo(() => dispatchers.find((d) => d.dispatcher_id === myId), [dispatchers, myId]);

  function openTruck(truckId, tab) {
    if (!truckId) return;
    navigate(`/dispatch/operational-center?truck=${truckId}&tab=${tab || "overview"}`);
  }
  function openDispatcherAlerts(dispatcherId) {
    navigate("/dispatch/alerts", { state: { targetDispatcherId: dispatcherId } });
  }

  if (loading) return <LoadingState label="Loading Control Tower…" />;

  return (
    <div>
      <div style={styles.header}>
        <h1 style={styles.title}>Fleet Control Tower</h1>
        <div style={styles.subtitle}>What's wrong right now, what it's costing, and where to look first.</div>
      </div>

      {/* 1. Fleet Health */}
      <div style={styles.healthStrip}>
        <HealthStat
          icon={<AlertOctagon size={16} />} color="#dc2626" value={alertCounts.critical} label="Critical Issues"
          onClick={() => document.getElementById("needs-attention")?.scrollIntoView({ behavior: "smooth" })}
        />
        <HealthStat icon={<AlertTriangle size={16} />} color="#d97706" value={alertCounts.warning} label="Warnings" />
        <HealthStat icon={<Compass size={16} />} color="#dc2626" value={loadsAtRiskCount} label="Loads At Risk"
          title="Distinct loads whose live ETA still clears its real pickup/delivery appointment buffer, but is projected to miss it -- excludes loads whose appointment has already actually passed (those show under Action Required instead) and off-route/toll/PTO issues, which are real but don't threaten this delivery completing." />
        <HealthStat icon={<TruckIcon size={16} />} color="#16a34a" value={capacity?.trucks_count ?? 0} label="Available Trucks" />
        <HealthStat icon={<DollarSign size={16} />} color="#d97706" value={fmtMoney(impact24h?.by_alert_type?.reduce((s, r) => s + (r.estimated_cost_impact || 0), 0))} label="Current Estimated Exposure"
          title="Heuristic exposure from currently-open off-route episodes only -- a snapshot of live risk, not a historical total. Episodes that have already resolved no longer contribute, no matter how recently." />
      </div>

      {/* 2. Action Required -- realized operational exceptions (pickup_overdue),
          distinct from and higher priority than the projected-risk signals
          in Needs Attention below. See exceptionLoadIds/operationalExceptions
          above for why these loads never also appear in Needs Attention or
          the Loads At Risk count. */}
      <div style={styles.sectionLabel}>Action Required</div>
      <div style={styles.sectionSub}>
        Pickup appointments that have already passed with no confirmation -- a realized lifecycle exception, not a projected risk.
      </div>
      <div style={styles.card}>
        {sortedActionRequired.length === 0 && <div style={styles.emptyNote}>No overdue pickups right now.</div>}
        {sortedActionRequired.map((a) => {
          const hours = Math.floor((a.overdue_minutes || 0) / 60);
          const minutes = Math.round((a.overdue_minutes || 0) % 60);
          const headline = a.is_abandoned_candidate
            ? `Assigned load appears abandoned · ${hours}h ${minutes}m overdue`
            : `Pickup overdue · ${hours}h ${minutes}m`;
          return (
            <div key={a.alert_event_id} style={styles.attentionRow}>
              <span style={{ ...styles.severityDot, background: a.severity === "critical" ? "#dc2626" : a.severity === "warning" ? "#d97706" : "var(--muted)" }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={styles.attentionMsg}>
                  {headline}
                  {a.is_abandoned_candidate && <span style={styles.abandonedBadge}>Appears abandoned</span>}
                </div>
                <div style={styles.attentionMeta}>
                  {a.truck_unit_number ? `Truck #${a.truck_unit_number}` : "Unassigned"}
                  {a.location ? ` · ${a.location}${a.destination_location ? ` → ${a.destination_location}` : ""}` : ""}
                  {a.dispatcher_name ? ` · ${a.dispatcher_name}` : ""}
                </div>
              </div>
              <button style={styles.viewBtn} onClick={() => openTruck(a.truck_id, ALERT_TAB_BY_TYPE[a.type])}>
                View <ArrowRight size={12} />
              </button>
            </div>
          );
        })}
      </div>

      {/* 2b. Possible missed lifecycle -- stop-detection completion CANDIDATES.
          Visually distinct from Action Required (realized overdue) AND from
          "currently at facility": these trucks dwelled at the facility and
          then LEFT without a confirmed pickup/delivery. Alert-only -- Confirm/
          Dismiss live on the Alerts page (View deep-links there). */}
      {missedStopCandidates.length > 0 && (
        <>
          <div style={styles.sectionLabel}>Possible missed lifecycle</div>
          <div style={styles.sectionSub}>
            Truck dwelled at the facility then departed with no confirmed pickup/delivery. Confirm or dismiss on the Alerts page.
          </div>
          <div style={styles.card}>
            {missedStopCandidates.map((a) => {
              const leg = a.type === "possible_missed_pickup" ? "pickup" : "delivery";
              return (
                <div key={a.alert_event_id} style={styles.attentionRow}>
                  <span style={{ ...styles.severityDot, background: a.severity === "critical" ? "#dc2626" : a.severity === "warning" ? "#d97706" : "var(--muted)" }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={styles.attentionMsg}>
                      {`Possible missed ${leg} · ${a.confidence || "?"} confidence`}
                    </div>
                    <div style={styles.attentionMeta}>
                      {a.truck_unit_number ? `Truck #${a.truck_unit_number}` : "Unassigned"}
                      {a.dwell_minutes != null ? ` · dwelled ${fmtDuration(a.dwell_minutes * 60000)}` : ""}
                      {a.current_distance_miles != null ? ` · now ${Math.round(a.current_distance_miles)}mi away` : ""}
                      {a.closest_distance_miles != null ? ` · closest ${Number(a.closest_distance_miles).toFixed(1)}mi` : ""}
                    </div>
                  </div>
                  <button style={styles.viewBtn} onClick={() => navigate("/dispatch/alerts")}>
                    Review <ArrowRight size={12} />
                  </button>
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* 3. Needs Attention */}
      <div id="needs-attention" style={styles.sectionLabel}>Needs Attention</div>
      <div style={styles.card}>
        {sortedAlerts.length === 0 && <div style={styles.emptyNote}>Nothing needs attention right now.</div>}
        {pagedAlerts.map((a) => (
          <div key={a.alert_event_id} style={styles.attentionRow}>
            <span style={{ ...styles.severityDot, background: a.severity === "critical" ? "#dc2626" : a.severity === "warning" ? "#d97706" : "var(--muted)" }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={styles.attentionMsg}>{a.message || `${a.type} alert on truck ${a.truck_unit_number || "?"}`}</div>
              <div style={styles.attentionMeta}>
                {a.truck_unit_number ? `Truck #${a.truck_unit_number}` : "Fleet-wide"}
                {a.dispatcher_name ? ` · ${a.dispatcher_name}` : ""}
                {a.estimated_cost_impact ? ` · ~${fmtMoney(a.estimated_cost_impact)} estimated exposure` : ""}
                {a.verified_cost_impact ? ` · ${fmtMoney(a.verified_cost_impact)} verified` : ""}
              </div>
            </div>
            <button style={styles.viewBtn} onClick={() => openTruck(a.truck_id, ALERT_TAB_BY_TYPE[a.type])}>
              View <ArrowRight size={12} />
            </button>
          </div>
        ))}
      </div>
      {sortedAlerts.length > 0 && (
        <Pagination page={attentionPage} totalPages={attentionTotalPages} totalCount={sortedAlerts.length} pageSize={ATTENTION_PAGE_SIZE} onPageChange={setAttentionPage} />
      )}

      {/* 4. Available Capacity */}
      <div style={styles.sectionLabel}>Available Capacity</div>
      <div style={styles.sectionSub}>
        Trucks not in transit, not in the shop, with a driver assigned who isn't on PTO -- genuinely bookable right now, sorted longest-idle first.
      </div>
      <div style={styles.card}>
        {capacityByDispatcher.length === 0 && <div style={styles.emptyNote}>No available capacity right now.</div>}
        {capacityByDispatcher.map(([dispatcherName, trucks]) => (
          <div key={dispatcherName} style={styles.capacityGroup}>
            <div style={styles.capacityGroupHead}>{dispatcherName} — {trucks.length} truck{trucks.length === 1 ? "" : "s"}</div>
            {trucks.map((t) => (
              <div key={t.truck_id} style={styles.capacityRow} onClick={() => openTruck(t.truck_id, "overview")}>
                <span style={styles.capacityTruck}>#{t.unit_number}</span>
                <span style={styles.capacityDriver}>{t.driver_name || "Unassigned"}</span>
                <span style={styles.capacityIdle}>
                  {t.idle_since ? `available ${fmtDuration(Date.now() - new Date(t.idle_since).getTime())}` : "idle time unknown"}
                </span>
                <ChevronRight size={14} color="var(--muted)" />
              </div>
            ))}
          </div>
        ))}
      </div>
      {capacity?.total_count > 0 && (
        <Pagination page={capacity.page} totalPages={capacity.total_pages} totalCount={capacity.total_count} pageSize={capacity.page_size} onPageChange={setCapacityPage} />
      )}

      {admin ? (
        <>
          <div style={styles.sectionLabel}>Dispatcher Overview</div>
          <div style={{ ...styles.card, padding: "6px 4px", overflowX: "auto" }}>
            {dispatchers.length === 0 && <div style={{ ...styles.emptyNote, padding: 14 }}>No dispatcher activity yet this week.</div>}
            {dispatchers.length > 0 && (
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Dispatcher</th>
                    <th style={styles.thNum}>Trucks</th>
                    <th style={styles.thNum}>Active Loads</th>
                    <th style={styles.thNum}>Off-route</th>
                    <th style={styles.thNum}>Fuel adherence</th>
                    <th style={styles.thNum}></th>
                  </tr>
                </thead>
                <tbody>
                  {dispatchers.map((d) => (
                    <tr key={d.dispatcher_id} style={styles.clickableRow} onClick={() => openDispatcherAlerts(d.dispatcher_id)}>
                      <td style={{ ...styles.td, fontWeight: 700 }}>{d.dispatcher_name}</td>
                      <td style={styles.tdNum}>{d.trucks_owned}</td>
                      <td style={styles.tdNum}>{d.trucks_in_transit}</td>
                      <td style={styles.tdNum}>{d.off_route_rate != null ? `${d.off_route_rate.toFixed(1)}%` : "—"}</td>
                      <td style={styles.tdNum}>{d.fuel_stop_adherence_rate != null ? `${d.fuel_stop_adherence_rate.toFixed(1)}%` : "—"}</td>
                      <td style={{ ...styles.tdNum, color: "var(--muted)" }}><ChevronRight size={14} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      ) : (
        <>
          <div style={styles.sectionLabel}>My Performance vs. Company</div>
          <div style={styles.sectionSub}>Every bar is mine — the tick mark is the company average across active dispatchers.</div>
          {!me ? (
            <div style={styles.card}>
              <div style={styles.emptyNote}>You don't have any trucks assigned yet — once you do, your weekly numbers will show up here.</div>
            </div>
          ) : (
            <div style={styles.grid3}>
              <ComparisonBar
                label="$ / mile" value={me.price_per_mile} avg={fleet?.avg_price_per_mile}
                format={(v) => `$${v.toFixed(2)}`} higherIsBetter mode="pct"
                footnote="Your real negotiated rate this week -- what your pay is actually a cut of."
              />
              <ComparisonBar
                label="Profit / mile" value={me.profit_per_mile} avg={fleet?.avg_profit_per_mile}
                format={(v) => `$${v.toFixed(2)}`} higherIsBetter mode="pct"
                footnote="Margin health after real costs -- doesn't change your pay this week, but thin loads add up."
              />
              <ComparisonBar
                label="Profit / truck" value={me.profit_per_truck} avg={fleet?.avg_profit_per_truck}
                format={fmtMoney} higherIsBetter mode="pct"
                footnote="Profit divided by trucks that actually worked a load this week."
              />
            </div>
          )}
        </>
      )}

      {/* 6. Optimization Performance */}
      <div style={styles.sectionLabel}>Optimization Performance</div>
      <div style={styles.optimizationRow}>
        <HeroCard icon={<Fuel size={16} />} label="Fuel Plan Compliance" value={fuelCompliance?.fleet_adherence_rate != null ? `${fuelCompliance.fleet_adherence_rate.toFixed(1)}%` : "—"} />
        <HeroCard icon={<DollarSign size={16} />} label="Toll Exceptions (7d)" value={impact7d?.by_alert_type?.find((r) => r.alert_type === "toll_overage")?.count ?? 0}
          sub="count, not a percentage -- see below" />
      </div>

      {/* 7. Financial Impact */}
      <div style={styles.sectionLabel}>Financial Impact</div>
      <div style={styles.sectionSub}>
        Verified Impact is a real plan-vs-actual delta (toll spend vs. quote); Estimated Exposure is a heuristic (off-route deviation cost). Never summed together.
      </div>
      <div style={styles.financialRow}>
        <FinancialWindow label="Last 24h" data={impact24h} />
        <FinancialWindow label="Last 7 days" data={impact7d} />
        <FinancialWindow label="Last 30 days" data={impact30d} />
      </div>

      {/* 8. Fleet Economics / My Economics */}
      <div style={styles.sectionLabel}>{admin ? "Fleet Economics — this week" : "My Economics — this week"}</div>
      {admin ? (
        <div style={styles.heroGrid}>
          <HeroCard icon={<DollarSign size={16} />} label="Fleet revenue" value={fmtMoney(fleet?.revenue_total)} />
          <HeroCard icon={<TrendingUp size={16} />} label="Fleet profit" value={fmtMoney(fleet?.profit_total)} emphasis />
          <HeroCard icon={<BarChart3 size={16} />} label="Avg $ / mile" value={fleet?.avg_price_per_mile != null ? `$${fleet.avg_price_per_mile.toFixed(2)}` : "—"} />
          <HeroCard icon={<BarChart3 size={16} />} label="Avg profit / mile" value={fleet?.avg_profit_per_mile != null ? `$${fleet.avg_profit_per_mile.toFixed(2)}` : "—"} />
          <HeroCard icon={<ShieldAlert size={16} />} label="HOS compliance" value={fleet?.avg_hos_compliance_rate != null ? `${fleet.avg_hos_compliance_rate.toFixed(1)}%` : "—"} />
        </div>
      ) : (
        <div style={styles.heroGrid}>
          <HeroCard icon={<DollarSign size={16} />} label="My revenue booked" value={fmtMoney(me?.revenue_booked)} />
          <HeroCard icon={<TrendingUp size={16} />} label="My profit" value={fmtMoney(me?.profit)} emphasis />
          <HeroCard icon={<BarChart3 size={16} />} label="My $ / mile" value={me?.price_per_mile != null ? `$${me.price_per_mile.toFixed(2)}` : "—"} />
          <HeroCard icon={<BarChart3 size={16} />} label="My profit / mile" value={me?.profit_per_mile != null ? `$${me.profit_per_mile.toFixed(2)}` : "—"} />
          <HeroCard icon={<ShieldAlert size={16} />} label="My HOS compliance" value={me?.hos_compliance_rate != null ? `${me.hos_compliance_rate.toFixed(1)}%` : "—"} />
        </div>
      )}
    </div>
  );
}

// Ported from DispatcherHome.jsx (kept there too, that file is now unrouted
// but not deleted) -- only the "how am I doing vs company" 3-bar shape is
// reused here, not the secondary operations-quality bars/fuel/idle detail
// cards, which Control Tower already covers via Fleet Health and
// Optimization Performance -- re-adding all of it would recreate the
// "too much scorecard" problem this page exists to fix.
function ComparisonBar({ label, value, avg, format, higherIsBetter, mode, footnote }) {
  if (value == null || avg == null) {
    return (
      <div style={{ ...styles.card, ...styles.cmpCard }}>
        <div style={styles.cmpLabel}>{label}</div>
        <div style={{ ...styles.emptyNote, marginTop: 8 }}>Not enough data yet this week.</div>
      </div>
    );
  }
  const diff = mode === "point" ? value - avg : (avg !== 0 ? ((value - avg) / avg) * 100 : null);
  const good = higherIsBetter ? value >= avg : value <= avg;
  const max = Math.max(value, avg, 0.0001) * 1.15;
  const fillPct = Math.min(100, Math.max(3, (value / max) * 100));
  const markerPct = Math.min(100, (avg / max) * 100);
  const barColor = good ? "#16a34a" : "#dc2626";

  return (
    <div style={{ ...styles.card, ...styles.cmpCard }}>
      <div style={styles.cmpTop}>
        <div>
          <div style={styles.cmpLabel}>{label}</div>
          <div style={styles.cmpValue}>{format(value)}</div>
        </div>
        {diff != null && (
          <span style={{ ...styles.cmpBadge, color: good ? "#16a34a" : "#dc2626", background: good ? "#e9f8ee" : "#fdecec" }}>
            {good ? <ArrowUp size={11} /> : <ArrowDown size={11} />}
            {Math.abs(diff).toFixed(mode === "point" ? 1 : 0)}{mode === "point" ? "pt" : "%"} vs avg
          </span>
        )}
      </div>
      <div style={styles.cmpTrack}>
        <div style={{ ...styles.cmpFill, width: `${fillPct}%`, background: barColor }} />
        <div style={{ ...styles.cmpMarker, left: `${markerPct}%` }} title={`Company avg: ${format(avg)}`} />
      </div>
      {footnote && <div style={styles.cmpFoot}>{footnote}</div>}
    </div>
  );
}

function HealthStat({ icon, color, value, label, onClick, title }) {
  return (
    <div style={{ ...styles.healthStat, cursor: onClick ? "pointer" : "default" }} onClick={onClick} title={title}>
      <span style={{ ...styles.healthIcon, color }}>{icon}</span>
      <div>
        <div style={{ ...styles.healthValue, color }}>{value}</div>
        <div style={styles.healthLabel}>{label}</div>
      </div>
    </div>
  );
}

function HeroCard({ icon, label, value, sub, emphasis }) {
  return (
    <div style={{ ...styles.heroCard, ...(emphasis ? styles.heroCardEmphasis : {}) }}>
      <div style={styles.heroHead}>
        <span style={styles.heroLabel}>{label}</span>
        <span style={{ ...styles.heroIcon, ...(emphasis ? styles.heroIconEmphasis : {}) }}>{icon}</span>
      </div>
      <div style={{ ...styles.heroValue, ...(emphasis ? styles.heroValueEmphasis : {}) }}>{value}</div>
      {sub && <div style={styles.heroSub}>{sub}</div>}
    </div>
  );
}

function FinancialWindow({ label, data }) {
  const verified = (data?.by_alert_type || []).reduce((s, r) => s + (r.verified_cost_impact || 0), 0);
  const estimated = (data?.by_alert_type || []).reduce((s, r) => s + (r.estimated_cost_impact || 0), 0);
  return (
    <div style={styles.financialCard}>
      <div style={styles.financialLabel}>{label}</div>
      <div style={styles.financialLine}>
        <span style={styles.financialLineLabel}>Verified Impact</span>
        <span style={{ ...styles.financialLineValue, color: verified > 0 ? "#dc2626" : verified < 0 ? "#16a34a" : "var(--ink)" }}>{fmtMoney(verified)}</span>
      </div>
      <div style={styles.financialLine}>
        <span style={styles.financialLineLabel}>Estimated Exposure</span>
        <span style={{ ...styles.financialLineValue, color: "#d97706" }}>{fmtMoney(estimated)}</span>
      </div>
    </div>
  );
}

const styles = {
  header: { marginBottom: 18 },
  title: { fontSize: 22, fontWeight: 800, color: "var(--ink)", margin: 0 },
  subtitle: { fontSize: 13, color: "var(--muted)", marginTop: 4 },

  sectionLabel: { fontSize: 13, fontWeight: 800, color: "var(--ink)", margin: "26px 0 8px" },
  sectionSub: { fontSize: 12, color: "var(--muted)", marginTop: -4, marginBottom: 12, lineHeight: 1.5, maxWidth: 720 },
  emptyNote: { fontSize: 12.5, color: "var(--muted)" },
  card: { background: "var(--card)", border: "1px solid var(--line)", borderRadius: "var(--radius-lg)", padding: 16, boxShadow: "var(--shadow-xs)" },

  healthStrip: { display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 14, marginBottom: 6 },
  healthStat: {
    background: "var(--card)", border: "1px solid var(--line)", borderRadius: "var(--radius-lg)", padding: "14px 16px",
    display: "flex", alignItems: "center", gap: 10, boxShadow: "var(--shadow-xs)",
  },
  healthIcon: { flexShrink: 0 },
  healthValue: { fontSize: 20, fontWeight: 800, lineHeight: 1.1 },
  healthLabel: { fontSize: 10.5, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: 0.3, marginTop: 2 },

  attentionRow: { display: "flex", alignItems: "center", gap: 10, padding: "10px 0", borderBottom: "1px solid var(--line)" },
  severityDot: { width: 9, height: 9, borderRadius: "50%", flexShrink: 0 },
  attentionMsg: { fontSize: 12.5, color: "var(--ink)", fontWeight: 600 },
  attentionMeta: { fontSize: 11, color: "var(--muted)", marginTop: 2 },
  abandonedBadge: {
    display: "inline-block", marginLeft: 8, fontSize: 10, fontWeight: 800, color: "#dc2626",
    background: "rgba(220,38,38,0.1)", border: "1px solid rgba(220,38,38,0.3)", borderRadius: 999,
    padding: "2px 8px", textTransform: "uppercase", letterSpacing: 0.3, verticalAlign: "middle",
  },
  viewBtn: {
    display: "flex", alignItems: "center", gap: 4, fontSize: 11.5, fontWeight: 700, color: "var(--brand)",
    background: "rgba(29,111,216,0.08)", border: "1px solid rgba(29,111,216,0.25)", borderRadius: 8,
    padding: "6px 10px", cursor: "pointer", flexShrink: 0,
  },

  capacityGroup: { marginBottom: 12 },
  capacityGroupHead: { fontSize: 12.5, fontWeight: 700, color: "var(--ink)", padding: "6px 0" },
  capacityRow: {
    display: "flex", alignItems: "center", gap: 12, padding: "8px 10px", borderRadius: 8, cursor: "pointer",
  },
  capacityTruck: { fontSize: 12.5, fontWeight: 700, color: "var(--ink)", width: 90, flexShrink: 0 },
  capacityDriver: { fontSize: 12, color: "var(--ink-2)", flex: 1 },
  capacityIdle: { fontSize: 11.5, color: "var(--muted)", flexShrink: 0 },

  table: { width: "100%", borderCollapse: "collapse", fontSize: 12.5 },
  th: { textAlign: "left", fontSize: 10, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: 0.3, padding: "0 10px 8px", borderBottom: "1px solid var(--line)" },
  thNum: { textAlign: "right", fontSize: 10, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: 0.3, padding: "0 10px 8px", borderBottom: "1px solid var(--line)" },
  td: { padding: 10, borderBottom: "1px solid var(--line)", verticalAlign: "middle", color: "var(--ink-2)" },
  tdNum: { padding: 10, borderBottom: "1px solid var(--line)", verticalAlign: "middle", color: "var(--ink-2)", textAlign: "right", fontVariantNumeric: "tabular-nums" },
  clickableRow: { cursor: "pointer" },

  optimizationRow: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 },

  grid3: { display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 14 },
  cmpCard: { padding: "16px 18px" },
  cmpTop: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10, marginBottom: 10 },
  cmpLabel: { fontSize: 12.5, fontWeight: 700, color: "var(--ink-2)" },
  cmpValue: { fontSize: 20, fontWeight: 800, color: "var(--ink)", marginTop: 3 },
  cmpBadge: {
    fontSize: 11, fontWeight: 800, borderRadius: 999, padding: "4px 9px", display: "inline-flex",
    alignItems: "center", gap: 3, whiteSpace: "nowrap", flexShrink: 0,
  },
  cmpTrack: { position: "relative", height: 8, background: "var(--card-2)", borderRadius: 5, marginTop: 12 },
  cmpFill: { position: "absolute", top: 0, left: 0, height: "100%", borderRadius: 5 },
  cmpMarker: { position: "absolute", top: -4, width: 2, height: 16, background: "var(--ink)", opacity: 0.55, borderRadius: 2 },
  cmpFoot: { fontSize: 10.5, color: "var(--muted)", marginTop: 12, lineHeight: 1.4 },

  financialRow: { display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 14 },
  financialCard: { background: "var(--card)", border: "1px solid var(--line)", borderRadius: "var(--radius-lg)", padding: "14px 16px", boxShadow: "var(--shadow-xs)" },
  financialLabel: { fontSize: 10.5, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: 0.3, marginBottom: 8 },
  financialLine: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "4px 0" },
  financialLineLabel: { fontSize: 12, color: "var(--ink-2)" },
  financialLineValue: { fontSize: 14, fontWeight: 800 },

  heroGrid: { display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 14, marginBottom: 6 },
  heroCard: { background: "var(--card)", border: "1px solid var(--line)", borderRadius: "var(--radius-lg)", padding: "16px 18px", boxShadow: "var(--shadow-xs)" },
  heroCardEmphasis: { background: "linear-gradient(155deg, rgba(29, 111, 216,0.09), var(--card) 60%)", borderColor: "rgba(29, 111, 216,0.3)" },
  heroHead: { display: "flex", alignItems: "center", justifyContent: "space-between" },
  heroLabel: { fontSize: 10.5, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: 0.4 },
  heroIcon: { width: 26, height: 26, borderRadius: 8, background: "rgba(29, 111, 216,0.1)", color: "var(--brand)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 },
  heroIconEmphasis: { background: "var(--brand)", color: "#fff" },
  heroValue: { fontSize: 24, fontWeight: 800, color: "var(--ink)", marginTop: 6, letterSpacing: -0.3 },
  heroValueEmphasis: { color: "var(--brand)" },
  heroSub: { fontSize: 11, color: "var(--muted)", marginTop: 4 },
};
